import * as React from "react";

import { LinkWithLang } from "components/plasmic/shared/link-with-lang/client";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { BENTO_GRID_DEFAULTS, BG_02 } from "@stadium/shared/src/helpers/plasmic/static-slices";

import { BG02Props } from "@stadium/shared/src/components/plasmic/BG02/constants";
import { LinkWithLangType } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

const BG02: React.FC<Readonly<BG02Props>> = ({
  heading = BENTO_GRID_DEFAULTS[BG_02].heading,
  countryCard = {
    countryCardHeading: BENTO_GRID_DEFAULTS[BG_02].countryCardHeading,
    countryCardDescription: BENTO_GRID_DEFAULTS[BG_02].countryCardDescription,
    countryCardBackgroundColor: BENTO_GRID_DEFAULTS[BG_02].countryCardBackgroundColor,
    countryCardFlags: BENTO_GRID_DEFAULTS[BG_02].countryCardFlags,
    countryCardWorldImage: BENTO_GRID_DEFAULTS[BG_02].countryCardWorldImage,
    countryCardWorldImageAlt: BENTO_GRID_DEFAULTS[BG_02].countryCardWorldImageAlt,
  },
  brandCard = {
    brandCardLabel: BENTO_GRID_DEFAULTS[BG_02].brandCardLabel,
    brandCardCount: BENTO_GRID_DEFAULTS[BG_02].brandCardCount,
    brandCardSubtext: BENTO_GRID_DEFAULTS[BG_02].brandCardSubtext,
    brandCardButtonText: BENTO_GRID_DEFAULTS[BG_02].brandCardButtonText,
    brandCardButtonLinkType: BENTO_GRID_DEFAULTS[BG_02].brandCardButtonLinkType as LinkWithLangType,
    brandCardButtonOpenInNewTab: BENTO_GRID_DEFAULTS[BG_02].brandCardButtonOpenInNewTab,
    brandCardButtonOtherUrl: BENTO_GRID_DEFAULTS[BG_02].brandCardButtonOtherUrl,
    brandCardButtonCategorySlug: BENTO_GRID_DEFAULTS[BG_02].brandCardButtonCategorySlug,
    brandCardBackgroundColor: BENTO_GRID_DEFAULTS[BG_02].brandCardBackgroundColor,
    brandCardShirtImage: BENTO_GRID_DEFAULTS[BG_02].brandCardShirtImage,
    brandCardShirtImageAlt: BENTO_GRID_DEFAULTS[BG_02].brandCardShirtImageAlt,
  },
  brandsPlatform = {
    brandsPlatformHeading: BENTO_GRID_DEFAULTS[BG_02].brandsPlatformHeading,
    brandsPlatformBackgroundImage: BENTO_GRID_DEFAULTS[BG_02].brandsPlatformBackgroundImage,
    brandsPlatformImageAlt: BENTO_GRID_DEFAULTS[BG_02].brandsPlatformImageAlt,
  },
  testimonial = {
    testimonialQuote: BENTO_GRID_DEFAULTS[BG_02].testimonialQuote,
    testimonialAuthorName: BENTO_GRID_DEFAULTS[BG_02].testimonialAuthorName,
    testimonialAuthorCompany: BENTO_GRID_DEFAULTS[BG_02].testimonialAuthorCompany,
    testimonialAuthorImage: BENTO_GRID_DEFAULTS[BG_02].testimonialAuthorImage,
    testimonialAuthorCompanyLogo: "",
    testimonialAuthorCompanyAlt: "",
    testimonialBackgroundColor: BENTO_GRID_DEFAULTS[BG_02].testimonialBackgroundColor,
    testimonialSwagmagicMark: BENTO_GRID_DEFAULTS[BG_02].testimonialSwagmagicMark,
    quotationMark: "",
  },
  review = {
    reviewBackgroundImage: BENTO_GRID_DEFAULTS[BG_02].reviewBackgroundImage,
    reviewImageAlt: BENTO_GRID_DEFAULTS[BG_02].reviewImageAlt,
    reviewHeading: BENTO_GRID_DEFAULTS[BG_02].reviewHeading,
    reviewButtonText: BENTO_GRID_DEFAULTS[BG_02].reviewButtonText,
    reviewButtonUrl: BENTO_GRID_DEFAULTS[BG_02].reviewButtonUrl,
    reviewButtonLinkType: BENTO_GRID_DEFAULTS[BG_02].reviewButtonLinkType as LinkWithLangType,
    reviewButtonOpenInNewTab: BENTO_GRID_DEFAULTS[BG_02].reviewButtonOpenInNewTab,
    reviewButtonOtherUrl: BENTO_GRID_DEFAULTS[BG_02].reviewButtonOtherUrl,
    reviewButtonCategorySlug: BENTO_GRID_DEFAULTS[BG_02].reviewButtonCategorySlug,
  },
  className,
}) => {
  const filteredFlags = countryCard.countryCardFlags.filter((flag) => flag.icon);

  const flagClassNames = [
    styles.flagIconOne,
    styles.flagIconTwo,
    styles.flagIconThree,
    styles.flagIconFour,
  ];

  return (
    <section className={`${styles.sectionWrapper} ${className || ""}`}>
      <div className={styles.divWrapper}>
        <h2 className={styles.heading}>{heading}</h2>
        <div className={styles.cardsGrid}>
          {/* Card 1: Country Partners */}
          <div
            className={styles.countryCard}
            style={{ backgroundColor: countryCard.countryCardBackgroundColor }}
          >
            <div className={styles.cardContent}>
              <h3 className={styles.cardHeading}>{countryCard.countryCardHeading}</h3>
              <p className={styles.cardDescription}>{countryCard.countryCardDescription}</p>
            </div>
            <div className={styles.flagsContainer}>
              {filteredFlags.map((flag, index) => (
                <Multimedia
                  key={flag.alt || index}
                  type="image"
                  src={flag.icon}
                  alt={flag.alt}
                  className={flagClassNames[index]}
                />
              ))}
            </div>
            {countryCard.countryCardWorldImage && (
              <Multimedia
                type="image"
                src={countryCard.countryCardWorldImage}
                alt={countryCard.countryCardWorldImageAlt}
                className={styles.globalMapImage}
              />
            )}
          </div>

          {/* Card 2: Brand Count */}
          <div
            className={styles.brandCard}
            style={{ backgroundColor: brandCard.brandCardBackgroundColor }}
          >
            <div className={styles.cardContent}>
              <p className={styles.brandLabel}>{brandCard.brandCardLabel}</p>
              <h3 className={styles.brandCount}>{brandCard.brandCardCount}</h3>
              <p className={styles.brandSubtext}>{brandCard.brandCardSubtext}</p>
            </div>
            {brandCard.brandCardButtonText && brandCard.brandCardButtonLinkType && (
              <div className={styles.buttonContainer}>
                <LinkWithLang
                  type={brandCard.brandCardButtonLinkType}
                  data={{
                    code: brandCard.brandCardButtonCategorySlug,
                    url: brandCard.brandCardButtonOtherUrl,
                  }}
                  target={brandCard.brandCardButtonOpenInNewTab ? "_blank" : "_self"}
                  className={styles.linkGroup}
                >
                  <span className={styles.linkPill}>{brandCard.brandCardButtonText}</span>
                  <span className={styles.linkCircle}></span>
                </LinkWithLang>
              </div>
            )}
            {brandCard.brandCardShirtImage && (
              <Multimedia
                type="image"
                src={brandCard.brandCardShirtImage}
                alt={brandCard.brandCardShirtImageAlt || "Shirt"}
                className={styles.shirtImage}
              />
            )}
          </div>

          {/* Testimonial Card */}
          <div
            className={styles.testimonialCard}
            style={{ backgroundColor: testimonial.testimonialBackgroundColor }}
          >
            <div className={styles.testimonialContent}>
              <div className={styles.quotationMarkWrapper}>
                <Multimedia
                  type="image"
                  src={testimonial.quotationMark}
                  alt="quotationMark"
                  className={styles.quotationMark}
                />
              </div>
              <p className={styles.testimonialQuote}>{testimonial.testimonialQuote}</p>
              <div className={styles.authorContainer}>
                <div className={styles.authorWrapper}>
                  <div className={styles.authorImageWrapper}>
                    <Multimedia
                      type="image"
                      src={testimonial.testimonialAuthorImage}
                      alt={testimonial.testimonialAuthorName}
                      className={styles.image}
                    />
                  </div>
                  {testimonial.testimonialAuthorCompanyLogo && (
                    <div className={styles.authorCompanyLogoWrapper}>
                      <Multimedia
                        type="image"
                        src={testimonial.testimonialAuthorCompanyLogo}
                        alt={testimonial.testimonialAuthorCompanyAlt}
                        className={styles.image}
                      />
                    </div>
                  )}
                </div>
                <div className={styles.authorInfo}>
                  <p className={styles.authorName}>{testimonial.testimonialAuthorName}</p>
                  <p className={styles.authorCompany}>{testimonial.testimonialAuthorCompany}</p>
                </div>
              </div>
            </div>

            <div className={styles.swagmagicWrapper}>
              <div className={styles.swagmagicMarquee}>
                <Multimedia
                  type="image"
                  src={testimonial.testimonialSwagmagicMark}
                  alt="swagmagic-mark"
                  className={styles.swagmagicMark}
                />
                <Multimedia
                  type="image"
                  src={testimonial.testimonialSwagmagicMark}
                  alt="swagmagic-mark"
                  className={styles.swagmagicMark}
                />
              </div>
            </div>
          </div>

          {/* Review Card */}
          <div className={styles.reviewCard}>
            <Multimedia
              type="image"
              src={review.reviewBackgroundImage}
              alt={review.reviewImageAlt}
              className={styles.reviewBackground}
            />
            <div className={styles.reviewContent}>
              <div className={styles.buttonContainer}>
                <h1 className={styles.reviewContentHeading}>{review.reviewHeading || ""}</h1>
                {review.reviewButtonText && review.reviewButtonLinkType && (
                  <LinkWithLang
                    type={review.reviewButtonLinkType}
                    data={{
                      code: review.reviewButtonCategorySlug,
                      url: review.reviewButtonOtherUrl || review.reviewButtonUrl,
                    }}
                    target={review.reviewButtonOpenInNewTab ? "_blank" : "_self"}
                    className={styles.linkGroup}
                  >
                    <span className={styles.reviewLinkPill}>{review.reviewButtonText}</span>
                    <span className={styles.linkCircle}></span>
                  </LinkWithLang>
                )}
              </div>
            </div>
          </div>

          {/* Brands Platform Card */}
          <div className={styles.brandsCard}>
            <Multimedia
              type="image"
              src={brandsPlatform.brandsPlatformBackgroundImage}
              alt={brandsPlatform.brandsPlatformImageAlt || "Brands"}
              className={styles.brandsBackground}
            />
            <div className={styles.brandsOverlay}>
              <p className={styles.brandsText}>{brandsPlatform.brandsPlatformHeading}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BG02;
